<template>
    <AppJumbotron class="!pb-0">
        <div class="flex items-center">
            <div class="flex-grow max-w-4xl">
                <div class="w-full">
                    <h1 class="text-white text-3xl md:text-5xl !leading-tight font-bold mb-8">
                        <span class="font-normal">Find reviews for</span> <span class="font-bold">{{
                            page.season
                        }} <br/> tyres</span> <span class="font-normal">at {{ config.name }}</span>
                    </h1>
                    <p class="text-white mb-4 font-normal"><span class="font-bold">{{ reviewCount }} reviews</span>
                        <br/> for {{ page.season }} tyres</p>
                    <AppButton class="my-4 bg-gradient-to-r from-green-300 to-green-200 text-dark !inline-block">Read Reviews</AppButton>
                </div>
            </div>
        </div>
    </AppJumbotron>
    <div class="bg-muted py-4 md:py-6">
        <AppContainer>
            <div class="grid md:grid-cols-3 gap-5">
                <div>
                    <h1 class="block text-black md:text-4xl font-bold mb-5">{{ headingTag }}</h1>
                    <div class="flex-none font-light" v-html="page.description"></div>
                    <div v-if="(page.cta_text) && (page.cta_link)">
                        <AppButton :href="page.cta_link"
                                   class="my-4 bg-gradient-to-r from-green-300 to-green-200 text-dark !inline-block">
                            {{ page.cta_text }}
                        </AppButton>
                    </div>
                </div>
                <div v-if="featuredVideo" class="col-span-2">
                    <AppVideo :type="featuredVideo.type" :clip="featuredVideo.video_id"></AppVideo>
                </div>
            </div>
        </AppContainer>
    </div>
    <AppContainer v-if="noneFeaturedVideos.length" class="py-4 md:py-6">
        <span class="text-3xl mb-4 block font-light"><span class="font-bold">More</span> {{
                page.season
            }} tyre videos</span>
        <div class="grid md:grid-cols-3 gap-5">
            <div v-for="video in noneFeaturedVideos">
                <AppVideo :type="video.type" :clip="video.video_id"></AppVideo>
                <span class="font-bold text-sm block mt-4">{{ video.title }}</span>
            </div>
        </div>
    </AppContainer>
    <AppContainer>
        <div class="grid grid-cols-none lg:grid-cols-2 gap-4 mt-4">
            <div>
                <span class="text-3xl mb-4 block font-light"><span class="font-bold">Highest rated</span> {{ page.season }} tyres</span>
                <p class="font-normal">The most popular {{ page.season }} tyres as voted by our users</p>
                <div class="mt-5">
                    <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                        <TyreCardLoader :items="9" :loading="loadHighestRatedProducts"></TyreCardLoader>
                        <TyreCard v-if="!loadHighestRatedProducts" v-for="product in topProducts" :product="product" />
                    </div>
                </div>
            </div>
            <div>
                <span class="text-3xl mb-4 block font-light"><span class="font-bold">Top reviews</span> for {{ page.season }} tyres</span>
                <p class="font-normal">The most helpful reviews as voted by our users</p>
                <div class="mt-5">
                    <ReviewCardLoader :items="3" :loading="loadTopReviews" />
                    <ReviewCard v-if="!loadTopReviews" v-for="review in topReviews" :review="review" />
                </div>
                <div v-if="!topReviews.length && !loadTopReviews" class="mt-5 bg-primary w-full text-center p-4">
                    <span class="text-white">No reviews found</span>
                </div>
            </div>
        </div>
    </AppContainer>
    <AppContainer class="mt-4">
        <span class="text-3xl mb-4 block font-light"><span class="font-bold">Latest reviews</span> for {{ page.season }} tyres</span>
        <p class="font-normal">The most recent reviews left by our users</p>
        <div class="mt-5">
            <MasonryWall v-if="latestReviews.length" :items="latestReviews" :ssr="{columns: 2}" :options="{padding: 12}" :gap="16">
                <template #default="{item, index}">
                    <ReviewCard :key="index" :review="item" />
                </template>
            </MasonryWall>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                <ReviewCardLoader :items="6" :loading="loadLatestReviews"></ReviewCardLoader>
            </div>
            <div class="w-full mt-5" v-if="nextReviewPage">
                <AppButton class="mx-auto !w-auto" @click="loadMore">Load more reviews</AppButton>
            </div>
            <div id="reviewsBase"></div>
            <div v-if="!latestReviews.length && !loadLatestReviews" class="mt-5 bg-primary w-full text-center p-4">
                <span class="text-white">No reviews found</span>
            </div>
        </div>
    </AppContainer>
    <AppContainer>
        <span class="text-3xl mb-4 block font-light"><span class="font-bold">Top rated premium</span> {{ page.season }} tyres</span>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <TyreCardLoader :items="12" :loading="loadTopRatedPremium"></TyreCardLoader>
            <TyreCard v-if="!loadTopRatedPremium" v-for="product in premiumProducts" :product="product" />
        </div>
        <div v-if="loadTopRatedPremium && premiumProducts.length" class="mt-5 bg-primary w-full text-center p-4">
            <span class="text-white">No tyres found</span>
        </div>
    </AppContainer>
    <AppContainer>
        <span class="text-3xl mb-4 block font-light"><span class="font-bold">Top rated mid-range</span> {{ page.season }} tyres</span>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <TyreCardLoader :items="12" :loading="loadTopRatedMidRange"></TyreCardLoader>
            <TyreCard v-if="!loadTopRatedMidRange" v-for="product in midrangeProducts" :product="product" />
        </div>
        <div v-if="loadTopRatedMidRange && midrangeProducts.length" class="mt-5 bg-primary w-full text-center p-4">
            <span class="text-white">No tyres found</span>
        </div>
    </AppContainer>
    <AppContainer>
        <span class="text-3xl mb-4 block font-light"><span class="font-bold">Top rated budget</span> {{ page.season }} tyres</span>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <TyreCardLoader :items="12" :loading="loadBudgetRange"></TyreCardLoader>
            <TyreCard v-if="!loadBudgetRange" v-for="product in budgetProducts" :product="product" />
        </div>
        <div v-if="loadBudgetRange && budgetProducts.length" class="mt-5 bg-primary w-full text-center p-4">
            <span class="text-white">No tyres found</span>
        </div>
    </AppContainer>
</template>

<script>
import AppContainer from "@/Layouts/AppContainer";
import MainLayout from "@/Layouts/MainLayout";
import {Head} from "@inertiajs/inertia-vue3";
import AppButton from "@/Components/AppButton";
import AppJumbotron from "@/Components/AppJumbotron";
import AppVideo from "@/Components/AppVideo";
import TyreCardLoader from "@/Components/Loaders/TyreCardLoader";
import ReviewCardLoader from "@/Components/Loaders/ReviewCardLoader";
import ReviewCard from "@/Components/Reviews/ReviewCard";
import TyreCard from "@/Components/Products/Tyres/TyreCard";
import MasonryWall from "@yeger/vue-masonry-wall";
import AppHead from "@/Components/AppSeoTags";

export default {
    name: "ViewTyrePage",
    layout: MainLayout,
    components:
        {
            AppHead,
            TyreCard,
            ReviewCard,
            AppVideo,
            AppButton,
            AppContainer,
            AppJumbotron,
            Head,
            ReviewCardLoader,
            TyreCardLoader,
            MasonryWall
    },
    props:
        {
            page: Object,
            seoTags: Object,
            videos: Object,
            reviewCount: Number,
            config: Object
        },
    data() {
        return {
            loadTopReviews: false,
            loadHighestRatedProducts: false,
            loadLatestReviews: false,
            loadTopRatedPremium: false,
            loadTopRatedMidRange: false,
            loadBudgetRange: false,
            topReviews: [],
            topProducts: [],
            latestReviews: [],
            premiumProducts:[],
            midrangeProducts:[],
            budgetProducts:[],
            nextReviewPage: null,
            currentReviewPageNumber: 1
        }
    },
    computed: {
        headingTag() {
            if (this.seoTags?.h1_tag) return this.seoTags.h1_tag;

            return `${this.page.name}`;
        },
        featuredVideo() {
            return this.videos.find(video => video.featured);
        },
        noneFeaturedVideos() {
            return this.videos.filter(video => !video.featured);
        }
    },
    mounted()
    {
        this.getTopReviews();
        this.getHighestRated();
        this.getLatestReviews();
        this.getTopRatedPremiumProducts();
        this.getMidRangeProducts();
        this.getBudgetProducts();
    },
    methods: {
        async getTopReviews()
        {
            this.loadTopReviews = true;

            const response = await axios.get(this.route("pages.reviews.top", this.page));

            const topReviews = [...this.topReviews];

            topReviews.push(...response.data);

            this.topReviews = topReviews;

            this.loadTopReviews = false;
        },
        async getHighestRated()
        {
            this.loadHighestRatedProducts = true;

            const response = await axios.get(this.route("pages.products.top", this.page));

            const topProducts = [...this.topProducts];

            topProducts.push(...response.data);

            this.topProducts = topProducts;

            this.loadHighestRatedProducts = false;
        },
        async getLatestReviews()
        {
            this.loadLatestReviews = true;

            const response = await axios.get(this.route("pages.products.latest_reviews", this.page), {
                params:
                    {
                        page: this.currentReviewPageNumber
                    }
            });

            this.nextReviewPage = response.data.next_page_url;

            const latestReviews = [...this.latestReviews];
            latestReviews.push(...response.data.data);

            this.latestReviews = latestReviews;

            this.loadLatestReviews = false;
        },
        async getTopRatedPremiumProducts()
        {
            this.loadTopRatedPremium = true;

            const response = await axios.get(this.route("pages.products.price_range", this.page), {
                params:
                {
                    type: "premium"
                }
            })

            const premiumProducts = [...this.premiumProducts];
            premiumProducts.push(...response.data);

            this.premiumProducts = premiumProducts;

            this.loadTopRatedPremium = false;
        },
        async getMidRangeProducts()
        {
            this.loadTopRatedMidRange = true;

            const response = await axios.get(this.route("pages.products.price_range", this.page), {
                params:
                    {
                        type: "mid-range"
                    }
            })

            const midrangeProducts = [...this.midrangeProducts];
            midrangeProducts.push(...response.data);

            this.midrangeProducts = midrangeProducts;

            this.loadTopRatedMidRange = false;
        },
        async getBudgetProducts()
        {
            this.loadBudgetRange = true;

            const response = await axios.get(this.route("pages.products.price_range", this.page), {
                params:
                    {
                        type: "budget"
                    }
            })

            const budgetProducts = [...this.budgetProducts];
            budgetProducts.push(...response.data);

            this.budgetProducts = budgetProducts;

            this.loadBudgetRange = false;
        },
        loadMore()
        {
            this.currentReviewPageNumber++;

            this.getLatestReviews();
        }
    }
}
</script>

<style scoped>

</style>
